import React, { useState, useEffect } from "react";
import axios from 'axios';
import CountUp from 'react-countup';

import { styled } from '@mui/material/styles';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { API_URL } from '../apiConfig';
import { useInView } from 'react-intersection-observer';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import Container from '@mui/material/Container';
import { Parallax } from 'react-parallax';

import CustomTypography from './UI/Typography/CustomTypography';
import NewsletterButton from './UI/Buttons/NewsLetterButton';
import NewsletterModal from './UI/Modal/NewsLetterModal';
import CustomAlert from './UI/Alerts/CustomAlert';
import FramerMotion from "./UI/Animations/FramerMotion";
import LatestNewsListSection from "./UI/Animations/LatestNewsListSection";
import BannerCarousel from "./UI/Animations/BannerCarousel";
import PartnerCarousel from "./UI/Animations/PartnerCarousel";
import YouTubeWidget from "./UI/Widgets/YouTubeWidget";
import LinkedInWidget from './UI/Widgets/LinkedInWidget';
import TwitterWidget from './UI/Widgets/TwitterWidget';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';

const theme = createTheme({
  shape: {
    borderRadius: 5
  },
});

const partnerImages = [
  { filename: `${process.env.PUBLIC_URL}/images/Partners/1.png`, url: 'https://www.cityofathens.gr/who/etaireia-anaptyxis-kai-toyristikis-provolis-athinon-eata/' },
  { filename: `${process.env.PUBLIC_URL}/images/Partners/2.png`, url: 'https://www.aia.gr/el/traveler/flight-info/rtfi/The' },
  { filename: `${process.env.PUBLIC_URL}/images/Partners/3.png`, url: 'https://royalapollonialimassol.com' },
  { filename: `${process.env.PUBLIC_URL}/images/Partners/4.png`, url: 'https://brinks.gr' },
  { filename: `${process.env.PUBLIC_URL}/images/Partners/5.png`, url: 'https://www.police.gov.cy/police/police.nsf/index_gr/index_gr?opendocument' },
  { filename: `${process.env.PUBLIC_URL}/images/Partners/6.png`, url: 'https://www.astynomia.gr/' },
  { filename: `${process.env.PUBLIC_URL}/images/Partners/7.png`, url: 'https://www.feg-touristguides.com/' },
  { filename: `${process.env.PUBLIC_URL}/images/Partners/8.png`, url: 'https://www.hertz.gr/en/?gad_source=1&gclid=Cj0KCQjw3tCyBhDBARIsAEY0XNnrieqiXWHqTGfCYGsA8E4X9Ie1xPwNwH-tEQUpdLdfzbIi8MBcHZwaApfvEALw_wcB' },
  { filename: `${process.env.PUBLIC_URL}/images/Partners/9.png`, url: 'https://www.limassolmarina.com/' },
  { filename: `${process.env.PUBLIC_URL}/images/Partners/10.png`, url: 'https://www.jct.gr/' },
  { filename: `${process.env.PUBLIC_URL}/images/Partners/11.png`, url: 'https://mdat.gr/' },
  { filename: `${process.env.PUBLIC_URL}/images/Partners/12.png`, url: 'https://www.policja.pl/pol/english-version/4889,Polish-National-Police.html' },
  { filename: `${process.env.PUBLIC_URL}/images/Partners/13.png`, url: 'https://www.police-nationale.interieur.gouv.fr/' },
  { filename: `${process.env.PUBLIC_URL}/images/Partners/14.png`, url: 'https://www.thpa.gr/' },
  { filename: `${process.env.PUBLIC_URL}/images/Partners/15.png`, url: 'http://www.ubbsla.org/en/' },
  { filename: `${process.env.PUBLIC_URL}/images/Partners/16.png`, url: 'https://www.uniwa.gr/' },
  { filename: `${process.env.PUBLIC_URL}/images/Partners/17.png`, url: 'https://mykonos.gr/' },
  { filename: `${process.env.PUBLIC_URL}/images/Partners/18.png`, url: 'https://kemea.gr/en/' }
];

const bannerImages = [
  `${process.env.PUBLIC_URL}/images/BannerImages/bike.png`, 
  `${process.env.PUBLIC_URL}/images/BannerImages/car.jpg`,
  `${process.env.PUBLIC_URL}/images/BannerImages/cycling.jpg`
];

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#F9F9F9',
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,

  display: 'flex', // Make it a flex container
  flexDirection: 'column', // Align children in a column
  alignItems: 'center', // Center items horizontally
  justifyContent: 'center', // Center items vertically
}));

const Home = () => {
  const [openNewsletterForm, setOpenNewsletterForm] = useState(false);
  const [formDataFromModal, setFormDataFromModal] = useState({
    name: '',
    surname: '',
    email: '',
    phone: '',
    organizationName: '',
    comments: '',
  });
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertType, setAlertType] = useState('success');
  const [alertMessage, setAlertMessage] = useState('');

  // Function to open the alert
  const handleOpenAlert = (type, message) => {
    setAlertType(type);
    setAlertMessage(message);
    setAlertOpen(true);
  };

  // Function to open the newslettermodal
  const handleOpenNewsletterForm = () => {
    setOpenNewsletterForm(true);
  };

  //Function to close the newslettermodal
  const handleCloseNewsletterForm = () => {
    setOpenNewsletterForm(false);
  };

  // Callback function to handle data from the modal
  const handleModalData = (formData) => {
    // You can perform any additional operations with the form data here if needed
    submitFormData(formData); // Pass the form data to the submitFormData function
  };

  // Axios call to send form data to the server
  // Update your submitFormData function to use handleOpenAlert
  const submitFormData = (formData) => {
    axios.post(API_URL + '/partners/create', formData)
      .then((response) => {
        handleOpenAlert('success', 'Form submitted successfully!');
        handleCloseNewsletterForm();
        setFormDataFromModal({
          name: '',
          surname: '',
          email: '',
          phone: '',
          organizationName: '',
          comments: '',
        });
      })
      .catch((error) => {
        console.error('Error saving form data', error);
        handleOpenAlert('error', 'Failed to submit the form. Please try again.');
      });
  };

  const [ref, inView] = useInView({
    triggerOnce: true, // Only trigger the animation once
    threshold: 0.1, // Trigger when 10% of the component is visible
  });

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="xxl">

        {/* Outer Box Inside the Fluid Container */}
        <Box sx={{ flexGrow: 1, bgcolor: 'white' }}>

          {/* Welcome Section */}
          <Grid container>
            <Grid item xs={12}>
              <Item 
                style={{ 
                  width: '100%', 
                  height: '200px', 
                  background: 'linear-gradient(to bottom, #004466, #002445)', // Bigger gradient effect with lighter blue
                  marginTop: '60px', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  justifyContent: 'center', // Center content vertically
                  alignItems: 'flex-start', // Align content to the left
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                  <img 
                    src={process.env.PUBLIC_URL + '/images/logo.png'} 
                    alt="Project Hector Logo" 
                    style={{ height: '100px' }} 
                  />
                </div>
                <CustomTypography
                  variant="h4"
                  color="silver"
                  text={
                    <>
                      en<span style={{ color: 'orange' }}>H</span>ancing Prot
                      <span style={{ color: 'orange' }}>ECT</span>ion
                      <span style={{ color: 'orange' }}> O</span>f touristic Sites in Eu
                      <span style={{ color: 'orange' }}>R</span>ope
                    </>
                  }
                  style={{ textAlign: 'left' }} // Ensure text is aligned to the left
                />
              </Item>
            </Grid>
          </Grid>

          {/* 
          <Box sx={{ marginTop: 2 }}>
            <BannerCarousel images={bannerImages} />
          </Box>  
          */}
          <Box >
            <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <img 
                src={`${process.env.PUBLIC_URL}/images/BannerImages/bigbanner_v2.jpg`} 
                alt="Project Hector Banner" 
                style={{ 
                  width: '100%', 
                  maxHeight: '400px', 
                  objectFit: 'cover',
                  borderRadius: '12px' 
                }} 
              />
            </div>
          </Box>

          {/* Activities Section 
          <FramerMotion />

*/}
          {/* <Grid container spacing={2} style={{ paddingTop: '50px' }}>
            <Grid item xs={12}>
              <Item style={{ paddingTop: '130px', paddingBottom: '130px', backgroundImage: 'url("/images/BannerImages/2.jpg")', backgroundSize: 'cover', backgroundPosition: 'center' }}>
              </Item>
            </Grid>
          </Grid> */}

          {/* Latest News Section */}
          <Grid container spacing={2} style={{ paddingTop: '150px' }}>
            <Grid item xs={12}>
              <Box textAlign="center">
                <CustomTypography variant="h3" text="Latest News" color="#0651A0" underlineThickness={3} underline={true} />
              </Box>
            </Grid>
          </Grid>

          {/* Latest News List Section */}
          <LatestNewsListSection />

          {/* Mission and Vision Section */}
          {/* <Parallax blur={0} bgImage="/images/BannerImages/5.jpg" bgImageAlt="divider" strength={500} style={{ height: '500px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                
              </Grid>
            </Grid>
          </Parallax> */}
           {/*
          <Grid container spacing={2} style={{ paddingTop: '50px' }}>
            <Grid item xs={12}>
              <Item 
                style={{ 
                  paddingTop: '100px', 
                  paddingBottom: '100px', 
                  backgroundColor: '#002445', // Set the background color
                  backdropFilter: 'blur(10px)', // Apply blur effect
                  borderRadius: '16px', // Set rounded edges
                  WebkitBackdropFilter: 'blur(10px)' // For Safari support
                }}
              >
              </Item>
            </Grid>
          </Grid>
            */}
                  <ThemeProvider theme={theme}>
                      <Container maxWidth="lg">
                          <Grid container spacing={2} style={{ paddingTop: '50px' }}>
                              <Grid item xs={12}>
                                  <Box textAlign="center">
                                      <CustomTypography
                                          variant="h3"
                                          text="Project Hector"
                                          color="#0651A0"
                                          underline={true}
                                      />
                                      <br />
                                      <CustomTypography
                                          variant="h5"
                                          text="The HECTOR project (eHancing protECTion of touristic sites in euRope) is a 24-month European research project funded through the European Union's Internal Security Fund.
                                                The project's coordinator is the Hellenic Police in an expanded form of consortium (18 partners – 5 countries), with the participation of law enforcement agencies, research institutions, municipal authorities, and tourism operators.
                                                The HECTOR project aims to shield European tourism infrastructure, emphasizing strengthening cooperation between the private and public sectors to define security standards, procedures, and protocols (Security Model Plan for Tourism Industry)."
                                          color="black"
                                          sx={{ textAlign: "justify" }}
                                      />
                                  </Box>
                              </Grid>
                          </Grid>
                      </Container>
                  </ThemeProvider>


          {/* Section with Country Icons */}
          <Grid container spacing={2} style={{ paddingTop: '50px', justifyContent: 'center', alignItems: 'center' }}>
            <Grid item xs={12}>
              <Box textAlign="center">
                {/* <CustomTypography variant="h3" text="Action" color="#0651A0" underline={true} /> */}
              </Box>
            </Grid>
          </Grid>

          <Container maxWidth="lg"> {/* Adjust the maxWidth value as needed */}
            <Grid container spacing={15} style={{ paddingTop: '20px', justifyContent: 'center' }}>
              <Grid item xs={12} sm={12} md={4} lg={4}>
                <Tooltip title="Cyprus" placement="top">
                  <img src={`${process.env.PUBLIC_URL}/images/Countries/cyprus.png`} alt="Cyprus" style={{ width: '100%', height: 'auto' }} />
                </Tooltip>  
              </Grid>
              <Grid item xs={12} sm={12} md={4} lg={4}>
                <Tooltip title="Greece" placement="top">
                  <img src={`${process.env.PUBLIC_URL}/images/Countries/greece.png`} alt="Greece" style={{ width: '100%', height: 'auto' }} />
                </Tooltip>
              </Grid>
              <Grid item xs={12} sm={12} md={4} lg={4}>
                <Tooltip title="France" placement="top">
                  <img src={`${process.env.PUBLIC_URL}/images/Countries/france.png`} alt="France" style={{ width: '100%', height: 'auto' }} />
                </Tooltip>
              </Grid>
            </Grid>

            <Grid ref={ref} container spacing={2} style={{ paddingTop: '20px', justifyContent: 'center' }}>
              <Grid item xs={12} sm={12} md={3} lg={3}>
                <Box textAlign="center">
                  <CustomTypography
                    variant="h4"
                    text={
                      inView ? <CountUp start={0} end={5} duration={2} /> : '0'
                    }
                    color="black"
                  />
                  <CustomTypography variant="h6" text="Number of project host countries" color="grey" />
                </Box>
              </Grid>
              <Grid item xs={12} sm={12} md={3} lg={3}>
                <Box textAlign="center">
                  <CustomTypography
                    variant="h4"
                    text={
                      inView ? <CountUp start={0} end={18} duration={2} /> : '0'
                    }
                    color="black"
                  />
                  <CustomTypography variant="h6" text="Number of Partners" color="grey" />
                </Box>
              </Grid>
              <Grid item xs={12} sm={12} md={3} lg={3}>
                <Box textAlign="center">
                  <CustomTypography
                    variant="h4"
                    text={
                      inView ? <CountUp start={0} end={24} duration={2} /> : '0'
                    }
                    color="black"
                  />
                  <CustomTypography variant="h6" text="Duration in months (01.12.2023-31.11.2025)" color="grey" />
                </Box>
              </Grid>
              <Grid item xs={12} sm={12} md={3} lg={3}>
                <Box textAlign="center">
                  <CustomTypography
                    variant="h4"
                    text={
                      inView ? <CountUp start={0} end={2089198.54} duration={2} prefix="€" decimals={2} /> : '€0.00'
                    }
                    color="black"
                  />
                  <CustomTypography variant="h6" text="Total Cost" color="grey" />
                </Box>
              </Grid>
            </Grid>

            <Grid container spacing={15} style={{ paddingTop: '20px', paddingBottom: '60px', justifyContent: 'center' }}>
              <Grid item xs={12} sm={12} md={4} lg={4}>
                <Tooltip title="Bulgaria" placement="top">
                  <img src={`${process.env.PUBLIC_URL}/images/Countries/bulgaria.png`} alt="Bulgaria" style={{ width: '100%', height: 'auto' }} />
                </Tooltip>
              </Grid>
              <Grid item xs={12} sm={12} md={4} lg={4}>
                <Tooltip title="Poland" placement="top">
                  <img src={`${process.env.PUBLIC_URL}/images/Countries/poland.png`} alt="Poland" style={{ width: '100%', height: 'auto' }} />
                </Tooltip>
              </Grid>
            </Grid>
          </Container>

          {/* Generic Divider Section 
          <Parallax blur={0} bgImage="/images/BannerImages/4.jpg" bgImageAlt="divider" strength={500} style={{ height: '500px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
              </Grid>
            </Grid>
          </Parallax>*/}

          {/* <Grid container spacing={2} style={{ paddingTop: '50px' }}>
            <Grid item xs={12}>
              <Item style={{ paddingTop: '130px', paddingBottom: '130px', backgroundImage: 'url("/images/BannerImages/4.jpg")', backgroundSize: 'cover', backgroundPosition: 'center' }}>
              </Item>
            </Grid>
          </Grid> */}

          {/* Follow Us header Section 
          <Grid container spacing={2} style={{ paddingTop: '50px' }}>
            <Grid item xs={12}>
              <Box textAlign="center">
                <CustomTypography variant="h4" text="Follow Us" color="#0651A0" underline={true} />
              </Box>
            </Grid>
          </Grid>
            */}

<Parallax 
            blur={0} 
            bgImage={`${process.env.PUBLIC_URL}/images/BannerImages/2.jpg`}  
            bgImageAlt="divider" 
            strength={500} 
            style={{ 
              height: '500px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              borderRadius: '5px',
              overflow: 'hidden'
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {/* Your content */}
              </Grid>
            </Grid>
          </Parallax>
          <Container maxWidth="lg"> {/* Adjust the maxWidth value as needed */}
            {/* YouTube Widget Divider Section */}
            {/* <Grid container spacing={2} style={{ paddingTop: '50px' }}>
              <Grid item xs={12} sm={12} md={12}>
                <Item>
                  <YouTubeWidget />
                </Item>
              </Grid>
            </Grid> */}

            {/* LinkedIn Widget Section */}
            {/* <Grid container spacing={2} style={{ paddingTop: '50px' }}>
              <Grid item xs={12} md={12} lg={12} xl={12} style={{ marginBottom: '20px' }}>
                <Item>
                  <LinkedInWidget />
                </Item>
              </Grid>
            </Grid> */}

            {/* Twitter Widget Section */}
            {/* <Grid container spacing={2} style={{ paddingTop: '50px' }}>
              <Grid item xs={12} sm={12} md={12}>
                <Item>
                  <TwitterWidget />
                </Item>
              </Grid>
            </Grid> */}
           
            {/* Social Icons Section */}
             {/*
            <Grid container spacing={2} style={{ paddingTop: '50px', textAlign: 'center' }}>
              <Grid item xs={12}>
              <CustomTypography variant="h4" text="Follow Us" color="#0651A0" underline={true} />
              <Box 
                  display="flex" 
                  justifyContent="center" 
                  alignItems="center" 
                  style={{ padding: '20px', borderRadius: '8px' }} // Removed border for invisible outline
                >
                  <a href="https://www.youtube.com/@Hector-Project">
                    <YouTubeIcon style={{ width: '50px', height: '50px', marginRight: '5px', color: '#FF0000' }} /> // Set color to YouTube red
                  </a>
                  <a href="https://www.linkedin.com/company/hector-isf">
                    <LinkedInIcon style={{ width: '50px', height: '50px', marginRight: '5px' }} /> // Reduced margin
                  </a>
                  <a href="https://x.com/Hector_ISF">
                    <TwitterIcon style={{ width: '50px', height: '50px', marginRight: '5px' }} /> // Reduced margin
                  </a>
                  <a href="https://invite.viber.com/?g2=AQBrXyna0owpclMdFAZA7A%2B3ntsf0HjO6ySTb%2FEBEJab24O0d39aRvYsEvo4hQ4d">
                    <img src={`${process.env.PUBLIC_URL}/images/viber.png`} alt="Viber" style={{ width: '50px', height: '50px' }} />
                  </a>
                </Box>
              </Grid>
            </Grid>
            */}
          </Container>

          {/* Project Partners Title Section */}
          <Grid container spacing={2} style={{ paddingTop: '50px' }}>
            <Grid item xs={12}>
              <Box textAlign="center">
                <CustomTypography variant="h3" text="Partners" color="#0651A0" underline={true} />
              </Box>
            </Grid>
          </Grid>

          <ThemeProvider theme={theme}>
            <Container maxWidth="lg"> {/* Adjust the maxWidth value as needed */}
              {/* Project Partners Logos Section */}
              <Grid container spacing={2} style={{ paddingTop: '50px' }}>
                <Grid item xs={12} sm={12} md={12}>
                  {/* Partners Carousel */}
                  <PartnerCarousel partnerImages={partnerImages} />
                </Grid>
              </Grid>
            </Container>
          </ThemeProvider>

          {/* Join Our Network Section */}
          <Grid container spacing={2} style={{ paddingTop: '50px' }}>
            <Grid item xs={12} sm={6} md={12}>
              <Item style={{ paddingTop: '70px', paddingBottom: '70px' }}>
                <CustomTypography variant="h4" text="Join our Network" color="#0651A0" />
                <CustomTypography variant="h5" text="Join us and enter our Network of Tourist Operators" color="#0651A0" />
                <NewsletterButton 
                  variant="contained" 
                  onClick={handleOpenNewsletterForm} 
                >
                  Submit my Form
                </NewsletterButton>
              </Item>
            </Grid>
          </Grid>

          {/* Footer Section */}
          <Grid container spacing={2} style={{ paddingTop: '50px' }}>
            <Grid item xs={12} sm={12} md={12}>
              <Item style={{ paddingTop: '50px', paddingBottom: '50px', backgroundImage: `url(${process.env.PUBLIC_URL}/images/BannerImages/footer.jpg)`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
                <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" padding="20px">
                  <Box display="flex" alignItems="center">
                    <img src={`${process.env.PUBLIC_URL}/images/eu-flag.png`} alt="European Union Flag" style={{ width: '100px', height: 'auto', marginRight: '10px' }} />
                  </Box>
                  <Box display="flex" flexDirection="column" alignItems="flex-start" flex="1">
                    <CustomTypography variant="h7" text="This project is funded by the European Union's Internal Security Fund — Police." color="white" />
                  </Box>
                  <Box display="flex" flexDirection="column" alignItems="center" flex="1">
                    <CustomTypography variant="h7" text="Grant Agreement No. 101100558 — PROJECT HECTOR" color="white" />
                  </Box>
                  <Box display="flex" flexDirection="column" alignItems="flex-end" flex="1">
                    <CustomTypography variant="h7" text="The content of this webpage represents the views of the author only and is his/her sole responsibility. The European Commission does not accept any responsibility for use that may be made of the information it contains." color="white" />
                  </Box>
                </Box>
              </Item>
            </Grid>
          </Grid>

        </Box>
        {/* Newsletter Form Modal */}
        <NewsletterModal modalTitle={'Join Our Network of Tourist Operations'} open={openNewsletterForm} onClose={handleCloseNewsletterForm} onUpdateSuccess={handleModalData} />

        {/* Use the AlertSnackbar component */}
        <CustomAlert
          open={alertOpen}
          type={alertType}
          message={alertMessage}
          onClose={() => setAlertOpen(false)}
        />
      </Container>
    </ThemeProvider>
  );
};

export default Home;